import { showDialog } from '@/utils/dialogs';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router';
import {
  View,
  ActivityIndicator,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
  StyleSheet,
  TextInput,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { Text } from '@/components/ui/text';
import { WebAppShell } from '@/design-system/components';
import { reviewDesk } from '@/design-system';

// Services & Types
import { ICFProgressAPI, isIcfProgressAccessDeniedError } from '../../services/api/icfProgressAPI';
import {
  isProgressTeaserResponse,
  ProgressDashboardResponse,
  ProgressTeaserResponse,
} from '../../types/icfProgress';
import { ROUTES, reportRoute } from '../../navigation/routes';
import { formatLevel, LEVEL_BANDS, LEVEL_META, levelBandIndex, shortLevelLabel } from '../../utils/icfLevels';
import { useTranslation } from '../../hooks/useTranslation';
import { useAccessPolicy } from '../../hooks/useAccessPolicy';
import { useResponsive } from '../../hooks/useResponsive';
import { trackAuthEvent } from '../../services/analytics';
import UpgradeModal from '../../components/billing/UpgradeModal';
import CompetencySparkline from '../../components/progress/CompetencySparkline';
import { CrfReadinessCard } from '../../components/progress/CrfReadinessCard';
import { ProgressSessionsPanel } from '../../components/progress/ProgressSessionsPanel';
import { WhatToWorkOnNextCard } from '../../components/progress/WhatToWorkOnNextCard';
import { PracticeExamCard } from '../../components/progress/PracticeExamCard';
import { brand } from '../../design-system/theme/tokens';

// Professional Design Tokens
const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
};

// Competency keys for chart data
const COMP_KEYS: (keyof ProgressDashboardResponse['progress']['competency_scores'])[] = [
  'demonstrates_ethical_practice',
  'embodies_coaching_mindset',
  'establishes_maintains_agreements',
  'cultivates_trust_safety',
  'maintains_presence',
  'listens_actively',
  'evokes_awareness',
  'facilitates_client_growth',
];

const FETCH_THROTTLE_MS = 15000; // 15s minimum gap between automatic refreshes

/**
 * ICF progress (web only, D1): readiness hero with the level bar, competency
 * lanes, recent analyses (each opening its report), practice and CRF cards.
 */
export const ICFProgressScreenEnhanced: React.FC = () => {
  if (process.env.NODE_ENV !== 'production') {
    console.debug('[ICFProgressScreenEnhanced] render');
  }
  const router = useRouter();
  const searchParams = useLocalSearchParams<{ progress_nudge?: string; competency?: string }>();
  const { t, language } = useTranslation();
  const accessPolicy = useAccessPolicy();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboard, setDashboard] = useState<ProgressDashboardResponse | null>(null);
  const [teaser, setTeaser] = useState<ProgressTeaserResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [goalSaving, setGoalSaving] = useState(false);
  const [targetDateInput, setTargetDateInput] = useState('');
  const [portfolioExporting, setPortfolioExporting] = useState(false);
  const latestDashboardRef = useRef<ProgressDashboardResponse | null>(null);
  const latestTeaserRef = useRef<ProgressTeaserResponse | null>(null);
  const teaserShownRef = useRef(false);
  const nudgeClickTrackedRef = useRef(false);
  const paywallOpenedRef = useRef(false);

  // Track if initial load has been done to prevent duplicate calls
  const hasLoadedRef = React.useRef(false);
  const isMountedRef = React.useRef(true);
  const fetchStateRef = React.useRef({ inFlight: false, lastFetch: 0 });

  useEffect(() => {
    latestDashboardRef.current = dashboard;
  }, [dashboard]);

  useEffect(() => {
    latestTeaserRef.current = teaser;
  }, [teaser]);

  useEffect(() => {
    setTargetDateInput(dashboard?.readiness?.target_date || '');
  }, [dashboard?.readiness?.target_date]);

  useEffect(() => {
    if (nudgeClickTrackedRef.current || !searchParams.progress_nudge) return;
    nudgeClickTrackedRef.current = true;
    const competency = typeof searchParams.competency === 'string' && /^C[1-8]$/.test(searchParams.competency)
      ? searchParams.competency
      : undefined;
    trackAuthEvent('progress_nudge_clicked', {
      surface: String(searchParams.progress_nudge).slice(0, 32),
      competency_code: competency,
    });
  }, [searchParams.competency, searchParams.progress_nudge]);

  useFocusEffect(
    useCallback(() => {
      trackAuthEvent('progress_screen_viewed', {
        total_sessions_analyzed:
          latestDashboardRef.current?.progress?.total_sessions_analyzed
          ?? latestTeaserRef.current?.tracked_session_count,
        has_data: Boolean(latestDashboardRef.current || latestTeaserRef.current),
        locked: Boolean(latestTeaserRef.current),
      });
      return () => {};
    }, [])
  );

  // Responsive layout detection
  const { isPhone, isCompact } = useResponsive();
  const isTabletOrDesktop = !isPhone;

  // =================== DATA LOADING ===================

  const loadDashboard = useCallback(
    async (options: { force?: boolean; showSpinner?: boolean } = {}) => {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[ICFProgressScreenEnhanced] loadDashboard invoked', {
          options,
          inFlight: fetchStateRef.current.inFlight,
          lastFetch: fetchStateRef.current.lastFetch,
          sinceLastFetch: fetchStateRef.current.lastFetch
            ? Date.now() - fetchStateRef.current.lastFetch
            : null,
        });
      }
      if (!isMountedRef.current) return;

      const { force = false, showSpinner = false } = options;
      const now = Date.now();
      const { inFlight, lastFetch } = fetchStateRef.current;

      if (inFlight) {
        if (force && isMountedRef.current) {
          setRefreshing(false);
        }
        return;
      }

      if (!force && lastFetch && now - lastFetch < FETCH_THROTTLE_MS) {
        return;
      }

      fetchStateRef.current.inFlight = true;
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[ICFProgressScreenEnhanced] fetching dashboard', { force });
      }
      if (showSpinner) {
        setLoading(true);
      }

      try {
        setError(null);
        const data = await ICFProgressAPI.getDashboard({
          bypassCache: force,
          cacheMs: FETCH_THROTTLE_MS,
          allowTeaser: true,
        });
        if (isMountedRef.current) {
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[ICFProgressScreenEnhanced] dashboard fetch succeeded');
          }
          if (isProgressTeaserResponse(data)) {
            setTeaser(data);
            setDashboard(null);
            if (!teaserShownRef.current) {
              teaserShownRef.current = true;
              trackAuthEvent('progress_teaser_shown', {
                surface: 'progress_tab',
                tracked_session_count: data.tracked_session_count,
                has_insight: Boolean(data.insight),
              });
            }
          } else {
            setDashboard(data);
            setTeaser(null);
          }
          setAccessDenied(false);
          hasLoadedRef.current = true;
          fetchStateRef.current.lastFetch = Date.now();
        }
      } catch (e: any) {
        if (isMountedRef.current) {
          console.error('ICF Progress error:', e);
          if (process.env.NODE_ENV !== 'production') {
            console.debug('[ICFProgressScreenEnhanced] dashboard fetch failed', {
              message: e?.message,
            });
          }
          if (isIcfProgressAccessDeniedError(e)) {
            setAccessDenied(true);
            setDashboard(null);
            setTeaser(null);
            setError(null);
          } else if (e?.message?.includes('Rate limit')) {
            // Keep previously rendered content when available, but never turn
            // an initial 429 into a blank screen with no recovery action.
            setError(hasLoadedRef.current ? null : (e?.message || t('icfProgress.failedToLoad')));
          } else {
            setError(e?.message || t('icfProgress.failedToLoad'));
          }
          fetchStateRef.current.lastFetch = Date.now();
        }
      } finally {
        fetchStateRef.current.inFlight = false;
        if (isMountedRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    },
    [t]
  );

  // Initial load - only once on mount
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasLoadedRef.current) {
      // rely on shared API caching for the initial load so we don't spam the backend
      loadDashboard({ showSpinner: true });
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [loadDashboard]);

  // Refresh when tab becomes focused - but only after initial load is complete
  useFocusEffect(
    useCallback(() => {
      if (hasLoadedRef.current) {
        loadDashboard();
      }
      return undefined;
    }, [loadDashboard])
  );

  const onRefresh = useCallback(() => {
    if (!refreshing) {
      setRefreshing(true);
      loadDashboard({ force: true });
    }
  }, [loadDashboard, refreshing]);

  const handleProgressSessionsChanged = useCallback(async () => {
    await loadDashboard({ force: true });
  }, [loadDashboard]);

  const [upgradeVisible, setUpgradeVisible] = useState(false);

  useEffect(() => {
    if (!paywallOpenedRef.current || !accessPolicy.hasProgressFeature) return;
    paywallOpenedRef.current = false;
    trackAuthEvent('progress_paywall_converted', { surface: 'progress_tab' });
    void loadDashboard({ force: true });
  }, [accessPolicy.hasProgressFeature, loadDashboard]);

  const closeProgressPaywall = useCallback(() => {
    setUpgradeVisible(false);
    if (paywallOpenedRef.current && !accessPolicy.hasProgressFeature) {
      paywallOpenedRef.current = false;
      trackAuthEvent('progress_paywall_dismissed', { surface: 'progress_tab' });
    }
  }, [accessPolicy.hasProgressFeature]);

  const openProgressPaywall = useCallback(() => {
    trackAuthEvent('progress_teaser_clicked', { surface: 'progress_tab' });
    trackAuthEvent('progress_paywall_shown', { surface: 'progress_tab', required_tier: 'tier2' });
    paywallOpenedRef.current = true;
    setUpgradeVisible(true);
  }, []);

  const openLockedProgressTarget = useCallback(() => {
    if (accessPolicy.canManageSchoolBilling) {
      router.push(ROUTES.TABS_SCHOOL_MANAGEMENT as any);
      return;
    }

    if (accessPolicy.isB2C) {
      // Open the in-app upgrade paywall instead of dumping the user on Profile.
      openProgressPaywall();
      return;
    }

    router.push(ROUTES.TABS_DASHBOARD as any);
  }, [accessPolicy.canManageSchoolBilling, accessPolicy.isB2C, openProgressPaywall, router]);

  const renderProgressTeaser = (payload: ProgressTeaserResponse) => {
    const insightTitle = payload.insight?.competency_code || t('icfProgress.teaserInsightFallbackTitle');
    const teaserCard = (
      <View style={progressTeaserStyles.container}>
        <View style={progressTeaserStyles.headerRow}>
          <View style={progressTeaserStyles.icon}>
            <MaterialIcons name="insights" size={28} color={reviewDesk.accent} />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={progressTeaserStyles.eyebrow}>{t('icfProgress.teaserEyebrow')}</Text>
            <Text style={progressTeaserStyles.title}>{t('icfProgress.teaserTitle')}</Text>
          </View>
        </View>

        <View style={progressTeaserStyles.metricsRow}>
          <View style={progressTeaserStyles.metric}>
            <Text style={progressTeaserStyles.metricLabel}>{t('icfProgress.teaserTrackedSessions')}</Text>
            <Text style={progressTeaserStyles.metricValue}>{payload.tracked_session_count}</Text>
          </View>
          <View style={progressTeaserStyles.metric}>
            <Text style={progressTeaserStyles.metricLabel}>{t('icfProgress.estimatedLevel')}</Text>
            <Text style={progressTeaserStyles.metricValue}>{formatLevelLabel(payload.estimated_level)}</Text>
          </View>
        </View>

        {payload.insight ? (
          <View style={progressTeaserStyles.insightCard}>
            <Text style={progressTeaserStyles.insightLabel}>
              {insightTitle}
            </Text>
            <Text style={progressTeaserStyles.insightCopy}>{payload.insight.summary}</Text>
            <Text style={progressTeaserStyles.insightFootnote}>{t('icfProgress.teaserOneOfEight')}</Text>
          </View>
        ) : null}

        <WhatToWorkOnNextCard
          plan={payload.development_plan}
          locked
          testID="progress_teaser_development_plan_card"
        />

        <View style={progressTeaserStyles.blurFrame}>
          <View
            style={[
              progressTeaserStyles.shapeGrid,
              Platform.OS === 'web' ? ({ filter: 'blur(5px)' } as any) : { opacity: 0.34 },
            ]}
            pointerEvents="none"
          >
            {[0, 1, 2, 3].map((index) => (
              <View key={`locked-shape-${index}`} style={progressTeaserStyles.shapeCard}>
                <View style={[progressTeaserStyles.shapeLine, { width: `${74 - index * 6}%` }]} />
                <View style={progressTeaserStyles.shapeBar} />
                <View style={[progressTeaserStyles.shapeLine, { width: '48%' }]} />
              </View>
            ))}
          </View>
          <View style={progressTeaserStyles.blurOverlay}>
            <MaterialIcons name="lock" size={24} color={reviewDesk.accent} />
            <Text style={progressTeaserStyles.blurTitle}>{t('icfProgress.teaserLockedSectionsTitle')}</Text>
            <Text style={progressTeaserStyles.blurCopy}>{t('icfProgress.teaserLockedSectionsCopy')}</Text>
          </View>
        </View>

        <TouchableOpacity
          testID="icf-progress-teaser-upgrade-cta"
          style={progressTeaserStyles.primaryButton}
          onPress={openProgressPaywall}
          accessibilityRole="button"
        >
          <Text className="text-white" style={progressTeaserStyles.primaryButtonText}>
            {t('icfProgress.teaserUnlockCta')}
          </Text>
          <MaterialIcons name="arrow-forward" size={18} color="#FFFFFF" />
        </TouchableOpacity>
        <UpgradeModal
          visible={upgradeVisible}
          requiredTier="tier2"
          featureKey="icfProgress"
          onClose={closeProgressPaywall}
        />
      </View>
    );

    return <WebAppShell active="progress" contentMaxWidth="100%">{teaserCard}</WebAppShell>;
  };

  const renderLockedProgressAccess = () => {
    const isSchoolManaged = accessPolicy.isB2BUser;
    const title = isSchoolManaged
      ? t('icfProgress.lockedSchoolTitle')
      : t('icfProgress.lockedTitle');
    const copy = isSchoolManaged
      ? t('icfProgress.lockedSchoolCopy')
      : t('icfProgress.lockedCopy');
    const canAct = accessPolicy.isB2C || accessPolicy.canManageSchoolBilling;
    const cta = accessPolicy.canManageSchoolBilling
      ? t('icfProgress.lockedSchoolCta')
      : t('icfProgress.lockedCta');

    const card = (
      <View style={progressLockedStyles.card}>
        <View style={progressLockedStyles.icon}>
          <MaterialIcons name="lock-outline" size={32} color={reviewDesk.accent} />
        </View>
        <Text style={progressLockedStyles.title}>{title}</Text>
        <Text style={progressLockedStyles.copy}>{copy}</Text>
        <TouchableOpacity
          testID="icf-progress-upgrade-cta"
          style={canAct ? progressLockedStyles.primaryButton : progressLockedStyles.secondaryButton}
          onPress={openLockedProgressTarget}
          accessibilityRole="button"
        >
          <Text
            className={canAct ? 'text-white' : undefined}
            style={canAct ? progressLockedStyles.primaryButtonText : progressLockedStyles.secondaryButtonText}
          >
            {canAct ? cta : t('navigation.home')}
          </Text>
          {canAct ? <MaterialIcons name="arrow-forward" size={18} color="#FFFFFF" /> : null}
        </TouchableOpacity>
        <UpgradeModal
          visible={upgradeVisible}
          requiredTier="tier2"
          featureKey="icfProgress"
          onClose={closeProgressPaywall}
        />
      </View>
    );

    return (
      <WebAppShell active="progress" contentMaxWidth="100%">
        <View nativeID="progress_overview" testID="progress_overview">
          {card}
        </View>
      </WebAppShell>
    );
  };

  // =================== HELPER FUNCTIONS ===================

  // CORE-2: every level goes through normalizeLevelKey()/LEVEL_META.
  const formatLevelLabel = (level?: string | null): string => formatLevel(t, level);

  /**
   * Get the overall current level from competency_progress or overall_progress
   * Returns the most common level across competencies, or computes from overall_progress
   */
  const getOverallLevel = (): string => formatLevelLabel(getOverallLevelRaw());

  /** The raw level behind getOverallLevel(), for key-based bar positions. */
  const getOverallLevelRaw = (): string | null => {
    if (!dashboard) return null;

    // Try to get from overall_progress first
    const overallProgress = dashboard.overall_progress;
    if (overallProgress?.timeline?.length > 0) {
      const latestEntry = overallProgress.timeline[overallProgress.timeline.length - 1];
      if (latestEntry?.level) {
        return latestEntry.level;
      }
    }

    // Fall back to competency_progress - find most common level
    const competencyProgress = dashboard.competency_progress;
    if (competencyProgress && Object.keys(competencyProgress).length > 0) {
      const levelCounts: Record<string, number> = {};
      Object.values(competencyProgress).forEach((cp: any) => {
        if (cp?.current_level) {
          levelCounts[cp.current_level] = (levelCounts[cp.current_level] || 0) + 1;
        }
      });
      
      const mostCommon = Object.entries(levelCounts).sort((a, b) => b[1] - a[1])[0];
      if (mostCommon) {
        return mostCommon[0];
      }
    }

    return readiness?.estimated_level_key || readiness?.estimated_level || null;
  };

  /**
   * Get overall momentum direction and icon
   */
  const getOverallMomentum = (): { text: string; icon: string; color: string } => {
    if (!dashboard) {
      return { text: t('icfProgress.noMomentum'), icon: 'remove', color: '#9CA3AF' };
    }
    
    const overallProgress = dashboard.overall_progress;
    const direction = overallProgress?.momentum?.direction;
    
    switch (direction) {
      case 'up':
        return { text: t('icfProgress.momentumUp'), icon: 'trending-up', color: '#10B981' };
      case 'down':
        return { text: t('icfProgress.momentumDown'), icon: 'trending-down', color: '#EF4444' };
      case 'flat':
        return { text: t('icfProgress.momentumFlat'), icon: 'trending-flat', color: '#F59E0B' };
      default:
        return { text: t('icfProgress.noMomentum'), icon: 'remove', color: '#9CA3AF' };
    }
  };

  /**
   * Get overall consistency percentage
   */
  const getOverallConsistency = (): number => {
    if (!dashboard?.overall_progress?.consistency) return 0;
    return dashboard.overall_progress.consistency.percentage || 0;
  };

  /**
   * Get competency level for display in chart
   */
  const getCompetencyLevel = (competencyKey: string): { level: string; ordinal: number } => {
    const competencyProgress = dashboard?.competency_progress;
    if (!competencyProgress || !competencyProgress[competencyKey]) {
      return { level: '-', ordinal: -1 };
    }
    
    const cp = competencyProgress[competencyKey];
    return {
      level: cp.current_level ? formatLevelLabel(cp.current_level) : '-',
      ordinal: cp.current_level_ordinal ?? -1,
    };
  };

  /**
   * Get competency momentum for a specific competency
   */
  const getCompetencyMomentum = (competencyKey: string): string | null => {
    const cp = dashboard?.competency_progress?.[competencyKey];
    return cp?.momentum?.direction || null;
  };

  const extractFilename = (title?: string) => {
    if (!title) return '';
    const t = title.trim();
    const prefix = 'Analiz:';
    if (t.startsWith(prefix)) {
      const rest = t.substring(prefix.length).trim();
      return rest.replace(/\.\.\.$/, '').trim();
    }
    return t;
  };

  const getCompetencyLabels = () => [
    t('icfProgress.compEthics'),
    t('icfProgress.compMindset'),
    t('icfProgress.compAgreements'),
    t('icfProgress.compTrust'),
    t('icfProgress.compPresence'),
    t('icfProgress.compListening'),
    t('icfProgress.compAwareness'),
    t('icfProgress.compGrowth'),
  ];

  const getCompetencyFullLabel = (competencyKey?: string): string => {
    const labels = [
      t('icfProgress.compEthicsFull'),
      t('icfProgress.compMindsetFull'),
      t('icfProgress.compAgreementsFull'),
      t('icfProgress.compTrustFull'),
      t('icfProgress.compPresenceFull'),
      t('icfProgress.compListeningFull'),
      t('icfProgress.compAwarenessFull'),
      t('icfProgress.compGrowthFull'),
    ];
    const index = COMP_KEYS.indexOf(competencyKey as any);
    return index >= 0 ? labels[index] : competencyKey || '';
  };

  const getReadinessStatusLabel = (status?: string): string => {
    switch (status) {
      case 'demonstrated':
      case 'target_supported':
        return t('icfProgress.readinessStatusTargetSupported');
      case 'near_target':
        return t('icfProgress.readinessStatusNearTarget');
      case 'needs_review':
      case 'developing':
        return t('icfProgress.readinessStatusDeveloping');
      case 'needs_more_evidence':
        return t('icfProgress.readinessStatusNeedsEvidence');
      case 'no_data':
        return t('icfProgress.readinessStatusNoData');
      default:
        return t('icfProgress.readinessStatusDeveloping');
    }
  };

  const getConfidenceLabel = (confidence?: string): string => {
    switch (confidence) {
      case 'high':
        return t('icfProgress.confidenceHigh');
      case 'medium':
        return t('icfProgress.confidenceMedium');
      case 'low':
      default:
        return t('icfProgress.confidenceLow');
    }
  };

  const credentialTargets = [
    { key: 'acc', label: t('icfProgress.levelACC') },
    { key: 'pcc', label: t('icfProgress.levelPCC') },
    { key: 'mcc', label: t('icfProgress.levelMCC') },
  ] as const;

  const updateCredentialGoal = useCallback(
    async (targetLevel: 'acc' | 'pcc' | 'mcc', targetDate: string | null = targetDateInput || null) => {
      if (goalSaving) return;
      setGoalSaving(true);
      try {
        await ICFProgressAPI.updateGoal({ target_level: targetLevel, target_date: targetDate });
        trackAuthEvent('icf_goal_updated', {
          target_level: targetLevel,
          has_target_date: Boolean(targetDate),
        });
        await loadDashboard({ force: true });
      } catch (e: any) {
        setError(e?.message || t('icfProgress.goalUpdateFailed'));
      } finally {
        if (isMountedRef.current) {
          setGoalSaving(false);
        }
      }
    },
    [goalSaving, loadDashboard, t, targetDateInput]
  );

  const saveTargetDate = useCallback(async () => {
    const normalized = targetDateInput.trim();
    if (normalized) {
      const parsed = new Date(`${normalized}T00:00:00Z`);
      if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized) || Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== normalized) {
        showDialog(t('icfProgress.targetDateInvalid'));
        return;
      }
    }
    const targetLevel = dashboard?.readiness?.target_level_key;
    if (targetLevel !== 'acc' && targetLevel !== 'pcc' && targetLevel !== 'mcc') {
      showDialog(t('icfProgress.targetDateNeedsLevel'));
      return;
    }
    await updateCredentialGoal(targetLevel, normalized || null);
  }, [dashboard?.readiness?.target_level_key, t, targetDateInput, updateCredentialGoal]);

  const handleExportPortfolio = useCallback(async () => {
    if (portfolioExporting) return;
    setPortfolioExporting(true);
    try {
      const pdfBlob = await ICFProgressAPI.exportEvidencePortfolioPdf(language === 'tr' ? 'tr' : 'en');
      const filename = 'icf-development-portfolio.pdf';
      if (Platform.OS === 'web') {
        const url = URL.createObjectURL(pdfBlob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      } else {
        const { documentDirectory, EncodingType, writeAsStringAsync } = await import('expo-file-system/legacy');
        const Sharing = await import('expo-sharing');
        const fileUri = `${documentDirectory ?? ''}${filename}`;
        const reader = new FileReader();
        await new Promise<void>((resolve, reject) => {
          reader.onerror = () => reject(reader.error);
          reader.onloadend = async () => {
            try {
              const base64data = String(reader.result || '').split(',')[1];
              await writeAsStringAsync(fileUri, base64data, { encoding: EncodingType.Base64 });
              if (await Sharing.isAvailableAsync()) {
                await Sharing.shareAsync(fileUri, {
                  mimeType: 'application/pdf',
                  dialogTitle: t('icfProgress.exportPortfolio'),
                  UTI: 'com.adobe.pdf',
                });
              }
              resolve();
            } catch (error) {
              reject(error);
            }
          };
          reader.readAsDataURL(pdfBlob);
        });
      }
      trackAuthEvent('icf_portfolio_exported', {
        format: 'pdf',
        strength_count: dashboard?.readiness?.evidence_portfolio?.strength_examples?.length || 0,
        development_count: dashboard?.readiness?.evidence_portfolio?.development_examples?.length || 0,
      });
    } catch {
      showDialog(t('icfProgress.exportPortfolioFailed'));
    } finally {
      setPortfolioExporting(false);
    }
  }, [dashboard?.readiness?.evidence_portfolio, language, portfolioExporting, t]);

  const getTrendStateText = (): string => {
    const trendState = dashboard?.readiness?.trend_state;
    if (!trendState) return getOverallMomentum().text;
    if (trendState.status === 'locked') {
      return t('icfProgress.trendsLockedShort', {
        count: trendState.sessions_until_unlock,
      });
    }
    if (trendState.status === 'sparse') {
      return t('icfProgress.trendsSparseShort');
    }
    return getOverallMomentum().text;
  };

  const getPracticeCheckinStatus = (status?: string): { label: string; icon: string; color: string } => {
    switch (status) {
      case 'met':
        return { label: t('icfProgress.checkinMet'), icon: 'check-circle', color: '#10B981' };
      case 'partial':
        return { label: t('icfProgress.checkinPartial'), icon: 'adjust', color: '#3B82F6' };
      case 'missed':
        return { label: t('icfProgress.checkinMissed'), icon: 'priority-high', color: '#F59E0B' };
      default:
        return { label: t('icfProgress.checkinNotObserved'), icon: 'radio-button-unchecked', color: '#6B7280' };
    }
  };

  const looksLikeEnglishCopy = (value?: string | null) => {
    if (!value) return false;
    return /\b(upload|analyzed|coaching|session|readiness|pattern|client|practice|awareness|target)\b/i.test(value);
  };

  const localizedCopy = (value: string | undefined | null, fallbackKey: string) => {
    const fallback = t(fallbackKey);
    const trimmed = value?.trim();
    if (!trimmed || (language === 'tr' && looksLikeEnglishCopy(trimmed))) {
      return fallback;
    }
    return trimmed;
  };

  const getGoalJourneyCopy = (readinessValue: ProgressDashboardResponse['readiness']): string => {
    if (readinessValue?.target_date && readinessValue.days_until_target !== undefined && readinessValue.days_until_target !== null) {
      const target = formatLevelLabel(readinessValue.target_level || readinessValue.target_level_key);
      if (readinessValue.days_until_target < 0) {
        return t('icfProgress.targetCountdownOverdue', {
          count: Math.abs(readinessValue.days_until_target),
          target,
        });
      }
      return t('icfProgress.targetCountdown', {
        count: readinessValue.days_until_target,
        target,
      });
    }
    return localizedCopy(
      readinessValue?.goal_summary,
      'icfProgress.goalSummaryFallback',
    );
  };

  const getLaneStatusLabel = (status?: string): string => {
    switch (status) {
      case 'needs_review': return t('icfProgress.laneNeedsReview');
      case 'demonstrated': return t('icfProgress.laneDemonstrated');
      case 'stable_strength':
        return t('icfProgress.laneStableStrength');
      case 'emerging_strength':
        return t('icfProgress.laneEmergingStrength');
      case 'inconsistent':
        return t('icfProgress.laneInconsistent');
      case 'target_blocker':
        return t('icfProgress.laneTargetBlocker');
      case 'insufficient_evidence':
        return t('icfProgress.laneInsufficientEvidence');
      default:
        return t('icfProgress.laneEmergingStrength');
    }
  };

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case 'target_supported':
      case 'stable_strength':
        return '#10B981';
      case 'near_target':
      case 'emerging_strength':
        return '#3B82F6';
      case 'developing':
      case 'inconsistent':
        return '#F59E0B';
      case 'needs_more_evidence':
      case 'no_data':
      case 'insufficient_evidence':
        return '#6B7280';
      case 'target_blocker':
        return '#EF4444';
      default:
        return COLORS.primary;
    }
  };

  // =================== RENDER LOADING/ERROR ===================

  const renderProgressShellState = (kind: 'loading' | 'error') => (
    <WebAppShell active="progress" contentMaxWidth="100%">
      <View style={progressWebStyles.stateCard}>
        {kind === 'loading' ? (
          <ActivityIndicator size="large" color={COLORS.primary} />
        ) : (
          <View style={progressWebStyles.stateIcon}>
            <MaterialIcons name="error-outline" size={32} color={COLORS.primary} />
          </View>
        )}
        <Text style={progressWebStyles.stateTitle}>
          {kind === 'loading' ? t('icfProgress.loading') : error}
        </Text>
        {kind === 'error' && (
          <TouchableOpacity
            style={progressWebStyles.stateButton}
            onPress={() => {
              trackAuthEvent('progress_retry_clicked');
              loadDashboard({ force: true, showSpinner: true });
            }}
            accessibilityRole="button"
          >
            <Text style={progressWebStyles.stateButtonText}>{t('icfProgress.retry')}</Text>
          </TouchableOpacity>
        )}
      </View>
    </WebAppShell>
  );

  if (loading) {
    return renderProgressShellState('loading');
  }

  if (accessDenied) {
    return renderLockedProgressAccess();
  }

  if (error) {
    return renderProgressShellState('error');
  }

  if (teaser) {
    return renderProgressTeaser(teaser);
  }

  if (!dashboard) return null;

  const readiness = dashboard.readiness;
  const readinessBlockers = readiness?.competency_lanes
    ?.filter((lane) => lane.status === 'target_blocker' || lane.status === 'inconsistent' || lane.status === 'needs_review')
    .slice(0, 4) || [];
  const developmentPatterns = readiness?.recurring_patterns
    ?.filter((pattern) => pattern.pattern_type === 'development')
    .slice(0, 3) || [];

  // D1: web-only; the native render tree was deleted.
  const isCompactWeb = isCompact;
  const isTinyWeb = isPhone;
  const competencyCards = getCompetencyLabels().map((label, idx) => {
    const key = COMP_KEYS[idx];
    const lane = readiness?.competency_lanes?.find((item) => item.competency_key === key);
    // GRD-R06: C1 is never leveled (no level, no level sparkline); an ethics
    // veto shows the concern flag with its explanation instead.
    const isC1 = idx === 0;
    const ethicsConcern = isC1 && Boolean(lane?.ethics_concern);
    const level = isC1
      ? '-'
      : lane?.current_level ? formatLevelLabel(lane.current_level) : getCompetencyLevel(key).level;
    const status = lane?.status || 'insufficient_evidence';
    return {
      key,
      label,
      fullLabel: getCompetencyFullLabel(key),
      level,
      status,
      statusLabel: ethicsConcern ? t('icfProgress.laneEthicsConcern') : getLaneStatusLabel(status),
      color: ethicsConcern ? getStatusColor('target_blocker') : getStatusColor(status),
      timeline: isC1 ? [] : dashboard.competency_progress?.[key]?.timeline || [],
      icon: ethicsConcern ? 'flag' : idx === 0 ? 'groups' : idx === 1 ? 'psychology' : idx === 2 ? 'track-changes' : 'forum',
      summary: ethicsConcern
        ? t('icfProgress.laneEthicsConcernBody')
        : localizedCopy(
          lane?.latest_evidence?.[0]?.rationale,
          'icfProgress.webEvidenceFallback',
        ),
      ethicsConcern,
    };
  });
  const practiceCheckins = readiness?.practice_checkins?.slice(0, 2) || [];
  const shellRecentItems = dashboard.recent_analyses.slice(0, 4).map((analysis) => ({
    id: analysis.id,
    title: extractFilename(analysis.session_title) || `${t('icfProgress.session')} ${analysis.session_id}`,
    subtitle: analysis.last_analyzed_at ? new Date(analysis.last_analyzed_at).toLocaleDateString() : '',
    onPress: () => router.push(reportRoute(analysis.session_id) as never),
  }));
  // VIS-CORE-2 / CORE-16: short band codes on phones, full labels elsewhere.
  const levelOrder = LEVEL_BANDS.map((key) => (
    isTinyWeb ? shortLevelLabel(t, key) : t(LEVEL_META[key].labelKey)
  ));
  const currentLevelRaw = readiness?.estimated_level_key || readiness?.estimated_level || getOverallLevelRaw();
  const currentLevel = formatLevelLabel(currentLevelRaw);
  const targetLevel = readiness?.target_level || readiness?.target_level_key
    ? formatLevelLabel(readiness?.target_level || readiness?.target_level_key)
    : t('icfProgress.targetNotSet');
  // Bar position from the level key, never from the translated label (CORE-2).
  const activeLevelIndex = levelBandIndex(currentLevelRaw);

  return (
    <WebAppShell
      active="progress"
      recentItems={shellRecentItems}
      contentMaxWidth="100%"
    >
      <View style={[progressWebStyles.pageHeader, progressWebStyles.pageHeaderRow]}>
        <View style={{ flexShrink: 1, minWidth: 0 }}>
          <Text style={[progressWebStyles.pageTitle, isTinyWeb && progressWebStyles.pageTitleTiny]}>{t('icfProgress.webTitle')}</Text>
          <Text style={progressWebStyles.pageSubtitle}>
            {t('icfProgress.webSubtitle')}
          </Text>
        </View>
        <View testID="progress_header_actions" style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
          {accessPolicy.canExportIcfPerformanceTranscript && (
            <TouchableOpacity
              style={progressWebStyles.secondaryActionButton}
              onPress={handleExportPortfolio}
              disabled={portfolioExporting}
              accessibilityRole="button"
            >
              {portfolioExporting
                ? <ActivityIndicator size="small" color={reviewDesk.accent} />
                : <MaterialIcons name="picture-as-pdf" size={18} color={reviewDesk.accent} />}
              <Text style={progressWebStyles.secondaryActionText}>{t('icfProgress.exportPortfolio')}</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={progressWebStyles.primaryButton}
            onPress={() => router.push(ROUTES.TABS_LIBRARY as any)}
            accessibilityRole="button"
          >
            <MaterialIcons name="add" size={18} color="#FFFFFF" />
            <Text className="text-white" style={progressWebStyles.primaryButtonText}>
              {t('icfProgress.webUploadAnalysis')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {dashboard.level_up && (
        <View style={progressWebStyles.levelUpBanner}>
          <View style={progressWebStyles.levelUpIcon}>
            <MaterialIcons name="celebration" size={24} color="#047857" />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={progressWebStyles.levelUpTitle}>{t('icfProgress.levelUpTitle')}</Text>
            <Text style={progressWebStyles.levelUpCopy}>
              {t('icfProgress.levelUpCopy', {
                previous: formatLevelLabel(dashboard.level_up.previous_level),
                next: formatLevelLabel(dashboard.level_up.new_level),
              })}
            </Text>
          </View>
        </View>
      )}

      <View
        style={[progressWebStyles.heroCard, isCompactWeb && progressWebStyles.heroCardCompact]}
        nativeID="progress_overview"
        testID="progress_overview"
      >
        <View style={[progressWebStyles.heroLeft, isTinyWeb && progressWebStyles.heroLeftTiny]}>
          <View style={[progressWebStyles.heroIcon, isTinyWeb && progressWebStyles.heroIconTiny]}>
            <MaterialIcons name="star" size={42} color={reviewDesk.accent} />
          </View>
          <View style={progressWebStyles.heroTextBlock}>
            <Text style={progressWebStyles.heroTitle}>
              {currentLevel || t('icfProgress.webWaitingForData')}
            </Text>
            <Text style={progressWebStyles.heroCopy}>
              {localizedCopy(
                readiness?.summary,
                'icfProgress.webNoDataSummary',
              )}
            </Text>
          </View>
        </View>
        <View style={progressWebStyles.levelPanel}>
          <Text style={progressWebStyles.panelLabel}>
            {t('icfProgress.webLevelPanelTitle')}
          </Text>
          <View style={progressWebStyles.levelSegment}>
            {levelOrder.map((level, index) => {
              const isActive = index === activeLevelIndex;
              return (
                <View key={level} style={[progressWebStyles.levelSegmentItem, isActive && progressWebStyles.levelSegmentItemActive]}>
                  <Text
                    className={isActive ? 'text-white' : undefined}
                    numberOfLines={2}
                    style={[
                      progressWebStyles.levelSegmentText,
                      isTinyWeb && progressWebStyles.levelSegmentTextTiny,
                      isActive && progressWebStyles.levelSegmentTextActive,
                    ]}
                  >
                    {level}
                  </Text>
                </View>
              );
            })}
          </View>
          <View style={progressWebStyles.levelMetaRow}>
            <Text style={progressWebStyles.levelMeta}>
              {t('icfProgress.webTarget')}: {targetLevel}
            </Text>
            <TouchableOpacity
              style={progressWebStyles.textButton}
              onPress={() => router.push({
                pathname: ROUTES.PROGRESS_COMPETENCY_DETAIL,
                params: { competency: String(COMP_KEYS[0]) },
              })}
              accessibilityRole="button"
            >
              <Text style={progressWebStyles.textButtonText}>
                {t('icfProgress.webViewCompetencies')}
              </Text>
              <MaterialIcons name="chevron-right" size={18} color={reviewDesk.accent} />
            </TouchableOpacity>
          </View>
          {!!(readiness?.goal_summary || readiness?.target_date) && (
            <Text style={progressWebStyles.goalSummary}>
              {getGoalJourneyCopy(readiness)}
            </Text>
          )}
          <View style={progressWebStyles.targetButtons}>
            {credentialTargets.map((target) => {
              const active = readiness?.target_level_key === target.key;
              return (
                <TouchableOpacity
                  key={target.key}
                  style={[progressWebStyles.targetButton, active && progressWebStyles.targetButtonActive]}
                  onPress={() => updateCredentialGoal(target.key)}
                  disabled={goalSaving}
                  accessibilityRole="button"
                >
                  <Text style={[progressWebStyles.targetButtonText, active && progressWebStyles.targetButtonTextActive]}>
                    {target.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <View style={progressWebStyles.dateRow}>
            <View style={progressWebStyles.dateField}>
              <Text style={progressWebStyles.dateLabel}>{t('icfProgress.targetDate')}</Text>
              <TextInput
                value={targetDateInput}
                onChangeText={setTargetDateInput}
                placeholder={t('icfProgress.targetDatePlaceholder')}
                placeholderTextColor={reviewDesk.muted}
                style={progressWebStyles.dateInput}
                autoCapitalize="none"
                autoCorrect={false}
                accessibilityLabel={t('icfProgress.targetDate')}
                {...({ type: 'date' } as any)}
              />
            </View>
            <TouchableOpacity style={progressWebStyles.dateAction} onPress={saveTargetDate} disabled={goalSaving} accessibilityRole="button">
              <MaterialIcons name="check" size={16} color="#FFFFFF" />
              <Text style={progressWebStyles.dateActionText}>{t('icfProgress.saveTargetDate')}</Text>
            </TouchableOpacity>
            {!!readiness?.target_date && (
              <TouchableOpacity
                style={progressWebStyles.dateClearAction}
                onPress={() => {
                  setTargetDateInput('');
                  const level = readiness.target_level_key;
                  if (level === 'acc' || level === 'pcc' || level === 'mcc') updateCredentialGoal(level, null);
                }}
                disabled={goalSaving}
                accessibilityRole="button"
              >
                <Text style={progressWebStyles.dateClearText}>{t('icfProgress.clearTargetDate')}</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>

      <View style={progressWebStyles.priorityStack}>
        <CrfReadinessCard
          crfReadiness={readiness?.crf_readiness}
          onSessionsReanalyzed={handleProgressSessionsChanged}
          onCompetencyPress={(competencyKey) => router.push({
            pathname: ROUTES.PROGRESS_COMPETENCY_DETAIL,
            params: { competency: String(competencyKey) },
          })}
          testID="progress_crf_readiness_card"
        />
        <WhatToWorkOnNextCard
          plan={dashboard.development_plan}
          crfReadiness={readiness?.crf_readiness}
          testID="progress_development_plan_card"
        />
        <PracticeExamCard onPress={() => router.push(ROUTES.PROGRESS_PRACTICE_EXAM as any)} />
      </View>

      <View style={[progressWebStyles.metricsRow, isCompactWeb && progressWebStyles.metricsRowCompact]}>
        {[
          { label: t('icfProgress.webSessionsAnalyzed'), value: String(dashboard.progress.total_sessions_analyzed), icon: 'analytics' },
          { label: t('icfProgress.webConsistency'), value: `${getOverallConsistency()}%`, icon: 'check-circle' },
          { label: t('icfProgress.webMomentum'), value: getTrendStateText(), icon: getOverallMomentum().icon },
        ].map((item) => (
          <View key={item.label} style={[progressWebStyles.metricCard, isTinyWeb && progressWebStyles.metricCardTiny]}>
            <View style={progressWebStyles.metricIcon}>
              <MaterialIcons name={item.icon as any} size={20} color={reviewDesk.accent} />
            </View>
            <View>
              <Text style={progressWebStyles.metricLabel}>{item.label}</Text>
              <Text style={progressWebStyles.metricValue}>{item.value}</Text>
            </View>
          </View>
        ))}
      </View>

      <View style={[progressWebStyles.cardGrid, isCompactWeb && progressWebStyles.cardGridCompact]}>
        {competencyCards.map((card, index) => (
          <TouchableOpacity
            key={card.key}
            testID={`progress_competency_${card.key}`}
            style={[progressWebStyles.competencyCard, isCompactWeb && progressWebStyles.competencyCardCompact, { borderLeftColor: card.color }]}
            onPress={() => router.push({
              pathname: ROUTES.PROGRESS_COMPETENCY_DETAIL,
              params: { competency: String(card.key) },
            })}
            accessibilityRole="button"
          >
            <View style={progressWebStyles.competencyTop}>
              <View style={[progressWebStyles.competencyIcon, { backgroundColor: `${card.color}18` }]}>
                <MaterialIcons name={card.icon as any} size={24} color={card.color} />
              </View>
              <View style={progressWebStyles.competencyTextBlock}>
                <Text style={progressWebStyles.competencyTitle}>
                  {index + 1}. {card.fullLabel || card.label}
                </Text>
                <View style={[progressWebStyles.statusBadge, { backgroundColor: `${card.color}18` }]}>
                  <Text style={[progressWebStyles.statusBadgeText, { color: card.color }]}>
                    {card.statusLabel || card.level}
                  </Text>
                </View>
              </View>
            </View>
            {index !== 0 && (
              <CompetencySparkline
                timeline={card.timeline}
                emptyLabel={t('icfProgress.sparklineEmpty')}
                accessibilityLabel={t('icfProgress.sparklineAccessibility', { competency: card.fullLabel || card.label })}
              />
            )}
            <Text
              testID={card.ethicsConcern ? 'progress_c1_ethics_concern' : undefined}
              style={progressWebStyles.competencySummary}
              numberOfLines={3}
            >
              {card.summary}
            </Text>
            <View style={progressWebStyles.cardLink}>
              <Text style={progressWebStyles.cardLinkText}>{t('icfProgress.webDetails')}</Text>
              <MaterialIcons name="chevron-right" size={18} color={reviewDesk.accent} />
            </View>
          </TouchableOpacity>
        ))}
      </View>

      <ProgressSessionsPanel onProgressChanged={handleProgressSessionsChanged} />

      <View style={[progressWebStyles.bottomGrid, isCompactWeb && progressWebStyles.bottomGridCompact]}>
        <View style={progressWebStyles.largePanel}>
          <View style={progressWebStyles.panelHeader}>
            <View>
              <Text style={progressWebStyles.panelTitle}>
                {t('icfProgress.webRecentAnalyses')}
              </Text>
              <Text style={progressWebStyles.panelSubtitle}>
                {t('icfProgress.webRecentAnalysesSubtitle')}
              </Text>
            </View>
          </View>
          {dashboard.recent_analyses.length === 0 ? (
            <View style={progressWebStyles.emptyList}>
              <Text style={progressWebStyles.emptyTitle}>
                {t('icfProgress.webNoAnalysesYet')}
              </Text>
              <Text style={progressWebStyles.emptyText}>
                {t('icfProgress.webNoAnalysesYetBody')}
              </Text>
            </View>
          ) : (
            dashboard.recent_analyses.slice(0, 5).map((analysis) => (
              <TouchableOpacity
                key={analysis.id}
                testID={`progress_recent_analysis_${analysis.session_id}`}
                style={[progressWebStyles.analysisRow, isTinyWeb && progressWebStyles.analysisRowTiny]}
                onPress={() => router.push(reportRoute(analysis.session_id) as never)}
                accessibilityRole="button"
              >
                <View style={progressWebStyles.playIcon}>
                  <MaterialIcons name="play-arrow" size={18} color={reviewDesk.accent} />
                </View>
                <Text style={progressWebStyles.analysisTitle} numberOfLines={1}>
                  {extractFilename(analysis.session_title) || `${t('icfProgress.session')} ${analysis.session_id}`}
                </Text>
                <Text style={[progressWebStyles.analysisDate, isTinyWeb && progressWebStyles.analysisDateTiny]}>
                  {analysis.last_analyzed_at ? new Date(analysis.last_analyzed_at).toLocaleDateString() : ''}
                </Text>
                <Text style={progressWebStyles.analysisLink}>
                  {t('icfProgress.webViewAnalysis')}
                </Text>
                <MaterialIcons name="chevron-right" size={18} color={reviewDesk.accent} />
              </TouchableOpacity>
            ))
          )}
        </View>

        {practiceCheckins.length > 0 && <View style={[progressWebStyles.nextPanel, isCompactWeb && progressWebStyles.nextPanelCompact]}>
          <Text style={progressWebStyles.panelTitle}>
            {t('icfProgress.sinceLastSession')}
          </Text>
          <Text style={progressWebStyles.panelSubtitle}>
            {t('icfProgress.plan.checkinSubtitle')}
          </Text>
          <View style={progressWebStyles.checkinGroup}>
              {practiceCheckins.map((item) => {
                const status = getPracticeCheckinStatus(item.status);
                return (
                  <View key={`${item.priority}-${item.competency_key}-checkin`} style={progressWebStyles.checkinItem}>
                    <View style={[progressWebStyles.checkinIcon, { backgroundColor: `${status.color}18` }]}>
                      <MaterialIcons name={status.icon as any} size={18} color={status.color} />
                    </View>
                    <View style={progressWebStyles.checkinCopy}>
                      <Text style={progressWebStyles.checkinStatus} numberOfLines={1}>
                        {item.competency_code} · {status.label}
                      </Text>
                      <Text style={progressWebStyles.checkinText} numberOfLines={3}>
                        {localizedCopy(
                          item.summary,
                          'icfProgress.checkinFallback',
                        )}
                      </Text>
                    </View>
                  </View>
                );
              })}
          </View>
        </View>}
      </View>
    </WebAppShell>
  );
};

const progressWebStyles = StyleSheet.create({
  stateCard: {
    width: '100%',
    minHeight: 360,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    gap: 14,
  },
  stateIcon: {
    width: 70,
    height: 70,
    borderRadius: 35,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
  },
  stateTitle: {
    color: reviewDesk.ink,
    fontSize: 20,
    lineHeight: 27,
    fontWeight: '800',
    textAlign: 'center',
  },
  stateButton: {
    minHeight: 42,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  stateButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  primaryButton: {
    minHeight: 42,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '800',
  },
  secondaryActionButton: {
    minHeight: 42,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  secondaryActionText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  pageHeader: {
    marginBottom: 18,
  },
  pageHeaderRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
  },
  pageTitleTiny: {
    fontSize: 26,
  },
  pageTitle: {
    color: reviewDesk.ink,
    fontSize: 34,
    fontWeight: '800',
    letterSpacing: 0,
  },
  pageSubtitle: {
    color: reviewDesk.inkSoft,
    fontSize: 16,
    lineHeight: 23,
    marginTop: 8,
  },
  levelUpBanner: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#A7F3D0',
    backgroundColor: '#ECFDF5',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  levelUpIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#D1FAE5',
  },
  levelUpTitle: {
    color: '#065F46',
    fontSize: 15,
    fontWeight: '800',
  },
  levelUpCopy: {
    color: '#047857',
    fontSize: 14,
    lineHeight: 20,
    marginTop: 3,
  },
  heroCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 26,
    flexDirection: 'row',
    gap: 26,
    alignItems: 'center',
    marginBottom: 16,
  },
  heroCardCompact: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  heroLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 22,
  },
  heroLeftTiny: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 14,
  },
  heroIcon: {
    width: 94,
    height: 94,
    borderRadius: 47,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
  },
  heroIconTiny: {
    width: 72,
    height: 72,
    borderRadius: 36,
  },
  heroTextBlock: {
    flex: 1,
  },
  heroTitle: {
    color: reviewDesk.ink,
    fontSize: 28,
    fontWeight: '800',
  },
  heroCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    lineHeight: 23,
    marginTop: 8,
    maxWidth: 520,
  },
  levelPanel: {
    flex: 1,
    gap: 12,
  },
  panelLabel: {
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '800',
  },
  levelSegment: {
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    borderRadius: 8,
    overflow: 'hidden',
    flexDirection: 'row',
    minHeight: 42,
  },
  levelSegmentItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 1,
    borderRightColor: reviewDesk.line,
    backgroundColor: '#FFFFFF',
  },
  levelSegmentItemActive: {
    backgroundColor: reviewDesk.accent,
  },
  levelSegmentText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    fontWeight: '700',
  },
  levelSegmentTextActive: {
    color: '#FFFFFF',
  },
  levelSegmentTextTiny: {
    fontSize: 12,
    textAlign: 'center',
    paddingHorizontal: 2,
  },
  levelMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  levelMeta: {
    color: reviewDesk.muted,
    fontSize: 13,
  },
  goalSummary: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 19,
  },
  targetButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  targetButton: {
    minHeight: 34,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  targetButtonActive: {
    borderColor: reviewDesk.accent,
    backgroundColor: reviewDesk.accent,
  },
  targetButtonText: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '800',
  },
  targetButtonTextActive: {
    color: '#FFFFFF',
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
  },
  dateField: {
    flexGrow: 1,
    flexBasis: 190,
    maxWidth: 260,
  },
  dateLabel: {
    color: reviewDesk.ink,
    fontSize: 12,
    fontWeight: '800',
    marginBottom: 5,
  },
  dateInput: {
    minHeight: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    backgroundColor: '#FFFFFF',
    color: reviewDesk.ink,
    paddingHorizontal: 11,
    fontSize: 16, // >=16 stops iOS focus-zoom (FE-17)
  },
  dateAction: {
    minHeight: 40,
    borderRadius: 8,
    backgroundColor: reviewDesk.accent,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: 6,
    paddingHorizontal: 13,
  },
  dateActionText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
  dateClearAction: {
    minHeight: 40,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 12,
  },
  dateClearText: {
    color: reviewDesk.accent,
    fontSize: 13,
    fontWeight: '800',
  },
  textButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  textButtonText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  priorityStack: {
    gap: 14,
    marginBottom: 16,
  },
  metricsRow: {
    flexDirection: 'row',
    gap: 14,
    marginBottom: 16,
  },
  metricsRowCompact: {
    flexDirection: 'column',
  },
  metricCard: {
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  metricCardTiny: {
    alignItems: 'flex-start',
  },
  metricIcon: {
    width: 42,
    height: 42,
    borderRadius: 21,
    backgroundColor: reviewDesk.accentSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricLabel: {
    color: reviewDesk.muted,
    fontSize: 13,
    fontWeight: '700',
  },
  metricValue: {
    color: reviewDesk.ink,
    fontSize: 22,
    fontWeight: '800',
    marginTop: 2,
  },
  cardGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  cardGridCompact: {
    flexDirection: 'column',
  },
  competencyCard: {
    flexGrow: 1,
    flexBasis: 'calc(50% - 8px)',
    minWidth: 330,
    minHeight: 206,
    borderRadius: 12,
    borderWidth: 1,
    borderLeftWidth: 4,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 20,
  } as any,
  competencyCardCompact: {
    flexBasis: 'auto',
    minWidth: 0,
    width: '100%',
    alignSelf: 'stretch',
  } as any,
  competencyTop: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 14,
  },
  competencyIcon: {
    width: 54,
    height: 54,
    borderRadius: 27,
    alignItems: 'center',
    justifyContent: 'center',
  },
  competencyTextBlock: {
    flex: 1,
    minWidth: 0,
  },
  competencyTitle: {
    color: reviewDesk.ink,
    fontSize: 17,
    fontWeight: '800',
    lineHeight: 23,
    marginBottom: 8,
  },
  statusBadge: {
    alignSelf: 'flex-start',
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },
  statusBadgeText: {
    fontSize: 12,
    fontWeight: '800',
  },
  competencySummary: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    lineHeight: 21,
    minHeight: 62,
  },
  cardLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 16,
  },
  cardLinkText: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  bottomGrid: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },
  bottomGridCompact: {
    flexDirection: 'column',
  },
  largePanel: {
    flex: 1,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 16,
  },
  nextPanel: {
    width: 336,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 18,
  },
  nextPanelCompact: {
    width: '100%',
  },
  panelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  panelTitle: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
  },
  panelSubtitle: {
    color: reviewDesk.muted,
    fontSize: 13,
    marginTop: 4,
    lineHeight: 19,
  },
  analysisRow: {
    minHeight: 62,
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  analysisRowTiny: {
    flexWrap: 'wrap',
    paddingVertical: 12,
  },
  playIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    alignItems: 'center',
    justifyContent: 'center',
  },
  analysisTitle: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '700',
  },
  analysisDate: {
    width: 128,
    color: reviewDesk.inkSoft,
    fontSize: 13,
  },
  analysisDateTiny: {
    width: 'auto',
  },
  analysisLink: {
    color: reviewDesk.accent,
    fontSize: 14,
    fontWeight: '800',
  },
  emptyList: {
    minHeight: 180,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: reviewDesk.ink,
    fontSize: 18,
    fontWeight: '800',
  },
  emptyText: {
    color: reviewDesk.inkSoft,
    fontSize: 14,
    marginTop: 6,
  },
  practiceItem: {
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    paddingTop: 14,
    marginTop: 14,
  },
  checkinGroup: {
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    paddingTop: 14,
    marginTop: 14,
    gap: 10,
  },
  checkinHeading: {
    color: reviewDesk.ink,
    fontSize: 14,
    fontWeight: '800',
  },
  checkinItem: {
    flexDirection: 'row',
    gap: 10,
    alignItems: 'flex-start',
  },
  checkinIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkinCopy: {
    flex: 1,
    minWidth: 0,
  },
  checkinStatus: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '800',
  },
  checkinText: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 18,
    marginTop: 2,
  },
  practiceCode: {
    color: reviewDesk.accent,
    fontSize: 12,
    fontWeight: '800',
    marginBottom: 4,
  },
  practiceTitle: {
    color: reviewDesk.ink,
    fontSize: 15,
    fontWeight: '800',
    lineHeight: 21,
  },
  practiceCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 13,
    lineHeight: 19,
    marginTop: 5,
  },
});

const progressTeaserStyles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 920,
    alignSelf: 'center',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    padding: 22,
    gap: 18,
  },
  headerRow: { flexDirection: 'row', alignItems: 'center', gap: 13 },
  icon: {
    width: 52,
    height: 52,
    borderRadius: 26,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
  },
  eyebrow: { color: reviewDesk.accent, fontSize: 12, fontWeight: '800', textTransform: 'uppercase' },
  title: { color: reviewDesk.ink, fontSize: 24, lineHeight: 30, fontWeight: '800', marginTop: 2 },
  metricsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  metric: {
    flex: 1,
    minWidth: 150,
    borderRadius: 10,
    backgroundColor: reviewDesk.accentSoft,
    padding: 14,
  },
  metricLabel: { color: reviewDesk.muted, fontSize: 12, fontWeight: '700' },
  metricValue: { color: reviewDesk.ink, fontSize: 22, fontWeight: '800', marginTop: 3 },
  insightCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#BFDBFE',
    backgroundColor: '#EFF6FF',
    padding: 16,
  },
  insightLabel: { color: brand.primary, fontSize: 13, fontWeight: '800' },
  insightCopy: { color: '#1E3A8A', fontSize: 15, lineHeight: 22, marginTop: 6 },
  insightFootnote: { color: '#475569', fontSize: 12, lineHeight: 18, marginTop: 10 },
  practiceCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#FDE68A',
    backgroundColor: '#FFFBEB',
    padding: 16,
  },
  practiceEyebrow: { color: '#B45309', fontSize: 12, fontWeight: '800', textTransform: 'uppercase' },
  practiceTitle: { color: '#78350F', fontSize: 16, fontWeight: '800', marginTop: 5, marginBottom: 8 },
  practiceAction: { flexDirection: 'row', alignItems: 'flex-start', gap: 7, marginTop: 4 },
  practiceCopy: { flex: 1, color: '#78350F', fontSize: 13, lineHeight: 19 },
  lockedPracticeCopy: { color: '#92400E', fontSize: 12, fontWeight: '700', marginTop: 10 },
  blurFrame: {
    minHeight: 230,
    borderRadius: 14,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: reviewDesk.line,
    position: 'relative',
  },
  shapeGrid: { padding: 14, flexDirection: 'row', flexWrap: 'wrap', gap: 10 },
  shapeCard: {
    width: '48%',
    minHeight: 90,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    padding: 12,
    gap: 9,
    backgroundColor: '#FFFFFF',
  },
  shapeLine: { height: 8, borderRadius: 4, backgroundColor: '#94A3B8' },
  shapeBar: { height: 18, borderRadius: 6, backgroundColor: '#CBD5E1' },
  blurOverlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: 'rgba(255,255,255,0.38)',
  },
  blurTitle: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800', textAlign: 'center', marginTop: 8 },
  blurCopy: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19, textAlign: 'center', maxWidth: 520, marginTop: 5 },
  primaryButton: {
    minHeight: 48,
    borderRadius: 10,
    backgroundColor: reviewDesk.accent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingHorizontal: 18,
  },
  primaryButtonText: { color: '#FFFFFF', fontSize: 15, fontWeight: '800' },
});

const progressLockedStyles = StyleSheet.create({
  card: {
    width: '100%',
    minHeight: 360,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  icon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: reviewDesk.accentSoft,
    marginBottom: 18,
  },
  title: {
    color: reviewDesk.ink,
    fontSize: 26,
    lineHeight: 32,
    fontWeight: '800',
    textAlign: 'center',
  },
  copy: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    lineHeight: 22,
    textAlign: 'center',
    maxWidth: 520,
    marginTop: 10,
    marginBottom: 22,
  },
  primaryButton: {
    minHeight: 44,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderRadius: 9,
    backgroundColor: reviewDesk.accent,
    paddingHorizontal: 18,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '800',
  },
  secondaryButton: {
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 9,
    borderWidth: 1,
    borderColor: reviewDesk.lineStrong,
    paddingHorizontal: 18,
  },
  secondaryButtonText: {
    color: reviewDesk.accent,
    fontSize: 15,
    fontWeight: '800',
  },
});

export default ICFProgressScreenEnhanced;
